import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import Text from '@/components/common/Text';
import DatePicker from '@/components/common/DatePicker';
import { getUserList } from '@/apis/admin';
import { QUERY_KEY } from '@/constants/queryKey';
import * as S from './style';

export interface MonthlyBillFilterType {
  status?: string | string[];
  payment_method?: string;
  start_date?: string;
  end_date?: string;
  billing_month?: string; // Format: YYYY-MM
  user_id?: number;
  company_id?: number; // For company select
  search?: string; // Keep for Bill ID search
  min_amount?: number;
  max_amount?: number;
  overdue_only?: boolean;
  pending_confirmation_only?: boolean;
  page?: number;
  limit?: number;
}

interface MonthlyBillFiltersProps {
  filters: MonthlyBillFilterType;
  onFilterChange: (filters: MonthlyBillFilterType) => void;
  onReset: () => void;
}

const MonthlyBillFilters = ({
  filters,
  onFilterChange,
  onReset,
}: MonthlyBillFiltersProps) => {
  const [localFilters, setLocalFilters] = useState(filters);

  // Fetch company list for dropdown
  const { data: companyData } = useQuery({
    queryKey: [QUERY_KEY.ADMIN.USER_LIST, 'company-dropdown'],
    queryFn: () => getUserList({ limit: 1000, dropdown: 'true' }), // Get all users (backend excludes super admin)
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Sync localFilters with filters prop when it changes
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleChange = (key: string, value: any) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
  };

  const handleApply = () => {
    onFilterChange(localFilters);
  };

  const handleReset = () => {
    const resetFilters = {
      status: undefined,
      payment_method: undefined,
      start_date: undefined,
      end_date: undefined,
      billing_month: undefined,
      user_id: undefined,
      company_id: undefined,
      search: undefined,
      min_amount: undefined,
      max_amount: undefined,
      overdue_only: false,
      pending_confirmation_only: false,
    };
    setLocalFilters(resetFilters);
    onReset();
  };

  return (
    <S.FilterContainer>
      <S.FilterRow>
        <S.FilterItem>
          <Text fontSize='0.875rem' fontWeight={600}>
            회사:
          </Text>
          <S.Select
            value={localFilters.company_id || 'all'}
            onChange={(e) =>
              handleChange('company_id', e.target.value === 'all' ? undefined : parseInt(e.target.value))
            }
          >
            <option value='all'>전체</option>
            {companyData?.data?.data?.map((company) => (
              <option key={company.id} value={company.id}>
                {company.company}
              </option>
            ))}
          </S.Select>
        </S.FilterItem>

        <S.FilterItem>
          <Text fontSize='0.875rem' fontWeight={600}>
            결제 방식:
          </Text>
          <S.Select
            value={localFilters.payment_method || 'all'}
            onChange={(e) =>
              handleChange('payment_method', e.target.value === 'all' ? undefined : e.target.value)
            }
          >
            <option value='all'>전체</option>
            <option value='prepaid'>선불</option>
            <option value='postpaid'>후불</option>
          </S.Select>
        </S.FilterItem>

        <S.FilterItem>
          <Text fontSize='0.875rem' fontWeight={600}>
            상태:
          </Text>
          <S.Select
            value={
              Array.isArray(localFilters.status)
                ? localFilters.status[0]
                : localFilters.status || 'all'
            }
            onChange={(e) =>
              handleChange('status', e.target.value === 'all' ? undefined : e.target.value)
            }
          >
            <option value='all'>전체</option>
            <option value='pending'>대기</option>
            <option value='sent'>발송</option>
            <option value='pending_confirmation'>확인 대기</option>
            <option value='paid'>결제 완료</option>
            <option value='overdue'>연체</option>
            <option value='failed'>실패</option>
            <option value='rejected'>거부</option>
          </S.Select>
        </S.FilterItem>
      </S.FilterRow>

      <S.FilterRow>
        <S.FilterItem>
          <Text fontSize='0.875rem' fontWeight={600}>
            청구 월:
          </Text>
          <DatePicker
            value={localFilters.billing_month}
            onChange={(value) => handleChange('billing_month', value || undefined)}
            placeholder='연도-월'
            mode='month'
          />
        </S.FilterItem>

        <S.FilterItem>
          <Text fontSize='0.875rem' fontWeight={600}>
            최소 금액:
          </Text>
          <S.Input
            type='number'
            placeholder='0'
            value={localFilters.min_amount || ''}
            onChange={(e) =>
              handleChange('min_amount', e.target.value ? parseInt(e.target.value) : undefined)
            }
          />
        </S.FilterItem>

        <S.FilterItem>
          <Text fontSize='0.875rem' fontWeight={600}>
            최대 금액:
          </Text>
          <S.Input
            type='number'
            placeholder='0'
            value={localFilters.max_amount || ''}
            onChange={(e) =>
              handleChange('max_amount', e.target.value ? parseInt(e.target.value) : undefined)
            }
          />
        </S.FilterItem>
      </S.FilterRow>

      <S.FilterRow>
        <S.ButtonContainer>
          <S.Button onClick={handleApply}>적용</S.Button>
          <S.Button onClick={handleReset}>초기화</S.Button>
        </S.ButtonContainer>
      </S.FilterRow>
    </S.FilterContainer>
  );
};

export default MonthlyBillFilters;

